/* eslint-disable react/prop-types */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";
import { ChevronDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { dashboardLinks } from "@/components/lib/SidebarLinks";
import DashboardRangeTabs from "@/components/dashboard/dashboard-range-tabs";
import { Skeleton } from "@/components/ui/skeleton";
import useAuth from "@/hooks/useAuth";
import { fetchOrderReports } from "@/redux/slice/dashboard/dashboard-slice";

const colors = {
  pickup: "#22D3EE",
  table: "#A855F7",
  inHouse: "#6366F1",
  delivery: "#10B981",
  online: "#84CC16",
  totalOrders: "#FBBF24",
  barColor: "#5850EC",
};

const YEAR_COLORS = ["#3B82F6", "#A855F7", "#10B981", "#FBBF24"];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const value = Number(payload[0]?.value || 0);

    return (
      <div className="bg-[#1F2937] border border-white/10 px-4 py-2 rounded-md shadow-2xl flex flex-col items-center relative">
        <p className="text-white text-sm font-semibold">
          {value.toLocaleString()}
        </p>
        <p className="text-gray-400 text-[10px]">{label}</p>
        <div className="absolute -bottom-1 w-2 h-2 bg-[#1F2937] rotate-45 border-r border-b border-white/10"></div>
      </div>
    );
  }
  return null;
};

const ChartSkeleton = ({ height }) => (
  <div className="w-full" style={{ height }}>
    <Skeleton className="h-full w-full" />
  </div>
);

function OrderReports() {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const yearOptions = useMemo(
    () => [currentYear, currentYear - 1],
    [currentYear],
  );

  const recentMonths = useMemo(
    () =>
      [2, 1, 0].map((offset) => {
        const date = new Date(currentYear, currentMonth - 1 - offset, 1);
        return {
          value: date.getMonth() + 1,
          label: MONTH_NAMES[date.getMonth()],
        };
      }),
    [currentMonth, currentYear],
  );

  const [comparisonRange, setComparisonRange] = useState("weekly");
  const [comparisonYear, setComparisonYear] = useState(currentYear);
  const [comparisonMonth, setComparisonMonth] = useState(currentMonth);

  const [totalOrdersRange, setTotalOrdersRange] = useState("monthly");
  const [totalYear, setTotalYear] = useState(currentYear);
  const [totalMonth, setTotalMonth] = useState(currentMonth);

  const [compareYear1, setCompareYear1] = useState(currentYear - 1);
  const [compareYear2, setCompareYear2] = useState(currentYear);

  const hasLoadedInitialRef = useRef(false);

  const reportData = useSelector((state) => state.dashboard?.orderReports);
  const orderLoading = useSelector((state) => state.dashboard?.orderLoading);

  const restaurantId = user?.restaurant_id;

  const comparisonData = useMemo(
    () => reportData?.charts?.comparison?.[comparisonRange] || [],
    [reportData, comparisonRange],
  );

  const totalOrdersData = useMemo(
    () => reportData?.charts?.totalOrders?.[totalOrdersRange] || [],
    [reportData, totalOrdersRange],
  );

  const yearlyComparisonData = useMemo(
    () => reportData?.charts?.yearlyComparison?.yearly || [],
    [reportData],
  );

  const yearlyKeys = useMemo(() => {
    const firstRow = yearlyComparisonData?.[0] || {};
    return Object.keys(firstRow)
      .filter((key) => key !== "label")
      .sort((a, b) => Number(a.replace("v", "")) - Number(b.replace("v", "")));
  }, [yearlyComparisonData]);

  const totalOrdersFromFilteredData = useMemo(
    () =>
      totalOrdersData.reduce((acc, item) => acc + Number(item?.orders || 0), 0),
    [totalOrdersData],
  );

  const formatWeeklyAxisTick = (value) => {
    const numericValue = Number(value || 0);

    if (numericValue >= 1000) {
      const compactValue = numericValue / 1000;
      return `${Number.isInteger(compactValue) ? compactValue : compactValue.toFixed(1)}k`;
    }

    return `${numericValue}`;
  };

  const isInitialLoading = Boolean(orderLoading?.initial);
  const isComparisonLoading =
    isInitialLoading || Boolean(orderLoading?.comparison);
  const isTotalLoading = isInitialLoading || Boolean(orderLoading?.totalOrders);
  const isYearlyLoading =
    isInitialLoading || Boolean(orderLoading?.yearlyComparison);

  const requestComparisonData = (
    period,
    year = comparisonYear,
    month = comparisonMonth,
  ) => {
    const params =
      period === "monthly"
        ? { period: "monthly", year, month }
        : { period: "weekly" };

    dispatch(
      fetchOrderReports({
        restaurant_id: restaurantId,
        params,
        requestMeta: { chart: "comparison" },
      }),
    );
  };

  const requestTotalOrdersData = (
    period,
    year = totalYear,
    month = totalMonth,
  ) => {
    const params =
      period === "yearly"
        ? { period: "yearly", year }
        : { period: "monthly", year, month };

    dispatch(
      fetchOrderReports({
        restaurant_id: restaurantId,
        params,
        requestMeta: { chart: "totalOrders" },
      }),
    );
  };

  const requestYearlyComparisonData = (
    year1 = compareYear1,
    year2 = compareYear2,
  ) => {
    dispatch(
      fetchOrderReports({
        restaurant_id: restaurantId,
        params: {
          period: "yearly",
          compare_year1: year1,
          compare_year2: year2,
        },
        requestMeta: { chart: "yearlyComparison" },
      }),
    );
  };

  useEffect(() => {
    if (!restaurantId || hasLoadedInitialRef.current) return;

    dispatch(
      fetchOrderReports({
        restaurant_id: restaurantId,
        params: { period: "weekly" },
        requestMeta: { mode: "initial" },
      }),
    );

    hasLoadedInitialRef.current = true;
  }, [dispatch, restaurantId]);

  const handleComparisonRangeChange = (value) => {
    setComparisonRange(value);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (value === "weekly") {
      requestComparisonData("weekly");
      return;
    }

    requestComparisonData("monthly", comparisonYear, comparisonMonth);
  };

  const handleComparisonYearChange = (year) => {
    setComparisonYear(year);

    if (
      !hasLoadedInitialRef.current ||
      !restaurantId ||
      comparisonRange !== "monthly"
    )
      return;

    requestComparisonData("monthly", year, comparisonMonth);
  };

  const handleComparisonMonthChange = (month) => {
    setComparisonMonth(month);

    if (
      !hasLoadedInitialRef.current ||
      !restaurantId ||
      comparisonRange !== "monthly"
    )
      return;

    requestComparisonData("monthly", comparisonYear, month);
  };

  const handleTotalRangeChange = (value) => {
    setTotalOrdersRange(value);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (value === "yearly") {
      requestTotalOrdersData("yearly", totalYear);
      return;
    }

    requestTotalOrdersData("monthly", totalYear, totalMonth);
  };

  const handleTotalYearChange = (year) => {
    setTotalYear(year);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (totalOrdersRange === "yearly") {
      requestTotalOrdersData("yearly", year);
      return;
    }

    requestTotalOrdersData("monthly", year, totalMonth);
  };

  const handleTotalMonthChange = (month) => {
    setTotalMonth(month);

    if (
      !hasLoadedInitialRef.current ||
      !restaurantId ||
      totalOrdersRange !== "monthly"
    )
      return;

    requestTotalOrdersData("monthly", totalYear, month);
  };

  const handleCompareYear1Change = (year) => {
    setCompareYear1(year);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    requestYearlyComparisonData(year, compareYear2);
  };

  const handleCompareYear2Change = (year) => {
    setCompareYear2(year);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    requestYearlyComparisonData(compareYear1, year);
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={dashboardLinks} />}
      pageName={{ title: "Order Reports" }}
      paths={[
        { name: "Dashboard", path: "/dashboard/overview" },
        { name: "Reports & Analytics", path: "/dashboard/revenue-reports" },
        { name: "Order", path: "/dashboard/order-reports" },
      ]}
    >
      <div className="container mx-auto p-6 space-y-8  min-h-screen text-white">
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-medium">Order</h1>
          <div className="flex gap-3 items-center">
            {comparisonRange === "monthly" && (
              <>
                <div className="relative">
                  <select
                    className="bg-[#1A1A1A] rounded-md px-3 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                    value={comparisonMonth}
                    onChange={(e) =>
                      handleComparisonMonthChange(Number(e.target.value))
                    }
                  >
                    {recentMonths.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
                <div className="relative">
                  <select
                    className="bg-[#1A1A1A] rounded-md px-4 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                    value={comparisonYear}
                    onChange={(e) =>
                      handleComparisonYearChange(Number(e.target.value))
                    }
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              </>
            )}
            <DashboardRangeTabs
              options={[
                { label: "Weekly", value: "weekly" },
                { label: "Monthly", value: "monthly" },
              ]}
              value={comparisonRange}
              onChange={handleComparisonRangeChange}
              shape="full"
              className="border-white/10"
              buttonClassName="px-4 md:px-5 py-1.5 text-xs"
            />
          </div>
        </div>

        <div className="grid grid-cols-5 gap-4">
          {[
            { label: "Pick up", color: colors.pickup, key: "pickup" },
            { label: "Table", color: colors.table, key: "table" },
            { label: "In-House", color: colors.inHouse, key: "inHouse" },
            { label: "Delivery", color: colors.delivery, key: "delivery" },
            { label: "Online", color: colors.online, key: "online" },
          ].map((item) => (
            <div
              key={item.label}
              className="bg-[#1A1A1A] border border-white/5 rounded-lg p-5 h-32 flex flex-col justify-between"
            >
              <p className="text-gray-400 text-sm font-medium">{item.label}</p>
              {isInitialLoading && !reportData ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <p
                  className="text-2xl font-semibold"
                  style={{ color: item.color }}
                >
                  {Number(reportData?.stats?.[item.key] || 0).toLocaleString()}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="bg-[#1A1A1A]/40 border border-white/5 rounded-xl p-6">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-lg font-medium">
              {comparisonRange === "weekly"
                ? "Weekly Comparison"
                : "Monthly Comparison"}
            </h2>
            <div className="flex gap-4 text-[10px] uppercase tracking-wider text-gray-300">
              {Object.entries(colors)
                .slice(0, 5)
                .map(([key, color]) => (
                  <span key={key} className="flex items-center gap-1.5">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    {key === "inHouse"
                      ? "In-House"
                      : key.charAt(0).toUpperCase() + key.slice(1)}
                  </span>
                ))}
            </div>
          </div>

          <div className="h-[400px] w-full">
            {isComparisonLoading ? (
              <ChartSkeleton height={400} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                {comparisonRange === "weekly" ? (
                  <BarChart
                    data={comparisonData}
                    barCategoryGap="18%"
                    barGap={2}
                  >
                    <CartesianGrid
                      vertical={false}
                      stroke="#6B7280"
                      strokeOpacity={0.45}
                    />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#F3F4F6", fontSize: 12 }}
                      dy={10}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#F3F4F6", fontSize: 12 }}
                      tickFormatter={formatWeeklyAxisTick}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(255,255,255,0.03)" }}
                      content={<CustomTooltip />}
                    />
                    <Bar
                      dataKey="pickup"
                      fill={colors.pickup}
                      radius={[2, 2, 0, 0]}
                      barSize={14}
                    />
                    <Bar
                      dataKey="table"
                      fill={colors.table}
                      radius={[2, 2, 0, 0]}
                      barSize={14}
                    />
                    <Bar
                      dataKey="inHouse"
                      fill={colors.inHouse}
                      radius={[2, 2, 0, 0]}
                      barSize={14}
                    />
                    <Bar
                      dataKey="delivery"
                      fill={colors.delivery}
                      radius={[2, 2, 0, 0]}
                      barSize={14}
                    />
                    <Bar
                      dataKey="online"
                      fill={colors.online}
                      radius={[2, 2, 0, 0]}
                      barSize={14}
                    />
                  </BarChart>
                ) : (
                  <AreaChart data={comparisonData}>
                    <defs>
                      {Object.entries(colors)
                        .slice(0, 5)
                        .map(([key, color]) => (
                          <linearGradient
                            key={key}
                            id={`grad-${key}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor={color}
                              stopOpacity={0.15}
                            />
                            <stop
                              offset="95%"
                              stopColor={color}
                              stopOpacity={0}
                            />
                          </linearGradient>
                        ))}
                    </defs>
                    <CartesianGrid
                      vertical={false}
                      stroke="#333"
                      strokeOpacity={0.5}
                    />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#666", fontSize: 12 }}
                      dy={10}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#666", fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {Object.entries(colors)
                      .slice(0, 5)
                      .map(([key, color]) => (
                        <Area
                          key={key}
                          type="monotone"
                          dataKey={key}
                          stroke={color}
                          strokeWidth={2}
                          fill={`url(#grad-${key})`}
                          dot={false}
                        />
                      ))}
                  </AreaChart>
                )}
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-[#1A1A1A]/40 border border-white/5 rounded-xl p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <p className="text-gray-400 text-sm mb-1 font-medium">
                Total Orders
              </p>
              {isTotalLoading ? (
                <Skeleton className="h-10 w-28" />
              ) : (
                <p className="text-3xl font-bold text-[#FBBF24]">
                  {totalOrdersFromFilteredData.toLocaleString()}
                </p>
              )}
            </div>
            <div className="flex gap-3 items-center">
              {totalOrdersRange === "monthly" && (
                <div className="relative">
                  <select
                    className="bg-[#1A1A1A] rounded-md px-3 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                    value={totalMonth}
                    onChange={(e) =>
                      handleTotalMonthChange(Number(e.target.value))
                    }
                  >
                    {recentMonths.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              )}

              <div className="relative">
                <select
                  className="bg-[#1A1A1A] rounded-md px-3 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                  value={totalYear}
                  onChange={(e) =>
                    handleTotalYearChange(Number(e.target.value))
                  }
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                />
              </div>

              <div className="flex bg-[#1A1A1A] rounded-md p-1 h-fit">
                <DashboardRangeTabs
                  options={[
                    { label: "Monthly", value: "monthly" },
                    { label: "Yearly", value: "yearly" },
                  ]}
                  value={totalOrdersRange}
                  onChange={handleTotalRangeChange}
                  shape="full"
                  className="border-white/10"
                  buttonClassName="px-4 md:px-5 py-1.5 text-xs"
                />
              </div>
            </div>
          </div>
          <div className="h-[350px] w-full">
            {isTotalLoading ? (
              <ChartSkeleton height={350} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={totalOrdersData}>
                  <CartesianGrid
                    vertical={false}
                    stroke="#333"
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#666", fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#666", fontSize: 12 }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(255,255,255,0.02)" }}
                    content={<CustomTooltip />}
                  />
                  <Bar
                    dataKey="orders"
                    fill={colors.barColor}
                    radius={[2, 2, 0, 0]}
                    barSize={5}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-[#1A1A1A]/40 border border-white/5 rounded-xl p-6">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-lg font-medium">Orders Comparison Yearly</h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <select
                  className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-1.5 text-xs text-gray-300 appearance-none pr-7"
                  value={compareYear1}
                  onChange={(e) =>
                    handleCompareYear1Change(Number(e.target.value))
                  }
                >
                  {yearOptions.map((year) => (
                    <option key={`left-${year}`} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={12}
                  className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                />
              </div>
              <span className="text-xs text-gray-500">Vs</span>
              <div className="relative">
                <select
                  className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-1.5 text-xs text-gray-300 appearance-none pr-7"
                  value={compareYear2}
                  onChange={(e) =>
                    handleCompareYear2Change(Number(e.target.value))
                  }
                >
                  {yearOptions.map((year) => (
                    <option key={`right-${year}`} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={12}
                  className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                />
              </div>
            </div>
          </div>
          <div className="h-[400px] w-full">
            {isYearlyLoading ? (
              <ChartSkeleton height={400} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={yearlyComparisonData}>
                  <defs>
                    {yearlyKeys.map((key, idx) => (
                      <linearGradient
                        key={key}
                        id={`grad-${key}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={YEAR_COLORS[idx % YEAR_COLORS.length]}
                          stopOpacity={0.1}
                        />
                        <stop
                          offset="95%"
                          stopColor={YEAR_COLORS[idx % YEAR_COLORS.length]}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid
                    vertical={true}
                    horizontal={false}
                    stroke="#333"
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#666", fontSize: 12 }}
                    dy={15}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#666", fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {yearlyKeys.map((key, idx) => (
                    <Area
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={YEAR_COLORS[idx % YEAR_COLORS.length]}
                      strokeWidth={3}
                      fill={`url(#grad-${key})`}
                      dot={false}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="flex justify-center gap-6 mt-6 text-xs font-medium">
            {yearlyKeys.map((key, idx) => (
              <span key={key} className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{
                    backgroundColor: YEAR_COLORS[idx % YEAR_COLORS.length],
                  }}
                />
                {key.replace("v", "")}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Layout2>
  );
}

export default OrderReports;
